import React, { useState } from 'react';
import { MobileLayout } from '../components/MobileLayout';
import { Card, CardContent, CardFooter } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { MatchReason } from '../components/MatchReason';
import { Sparkles, Wrench, Zap, Users, ArrowLeft, CalendarClock, Check, Star, MapPin } from 'lucide-react';
import { useMaintenance, Provider, RequestStatus, MaintenanceRequest } from '../context/MaintenanceContext';
type Flow = 'home' | 'straordinaria' | 'urgente' | 'condivisa';
const STATUS_BADGE: Record<RequestStatus, {
  label: string;
  cls: string;
}> = {
  in_attesa_aggregazione: {
    label: 'In attesa di aggregazione',
    cls: 'bg-amber-50 text-amber-700 border-amber-200'
  },
  in_ricerca: {
    label: 'In ricerca fornitore',
    cls: 'bg-amber-50 text-amber-700 border-amber-200'
  },
  assegnata: {
    label: 'Assegnata',
    cls: 'bg-muted text-muted-foreground border-border'
  },
  pianificata: {
    label: 'Pianificata',
    cls: 'bg-emerald-50 text-emerald-700 border-emerald-200'
  },
  in_corso: {
    label: '● In corso',
    cls: 'bg-coral/5 text-coral border-coral/20'
  },
  completata: {
    label: 'Completata',
    cls: 'bg-emerald-50 text-emerald-700 border-emerald-200'
  }
};
const PROPERTIES = ['Loft Navigli', 'Bilo Porta Romana', 'Studio Isola'];
const PROPERTY_ZONE: Record<string, string> = {
  'Loft Navigli': 'Navigli',
  'Bilo Porta Romana': 'Porta Romana',
  'Studio Isola': 'Isola'
};
export function RichiediInterventoPage() {
  const {
    requests,
    openAggregations,
    providers,
    createRequest,
    joinAggregation,
    assignProvider
  } = useMaintenance();
  const [flow, setFlow] = useState<Flow>('home');
  return <MobileLayout data-id="element-4114">
      <div className="mb-6" data-id="element-4115">
        {flow !== 'home' ? <button onClick={() => setFlow('home')} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-navy mb-3 transition-colors" data-id="element-4116">
            <ArrowLeft size={16} data-id="element-4117" />
            Richiedi intervento
          </button> : null}
        <h1 className="text-xl lg:text-2xl font-bold text-navy" data-id="element-4118">
          {flow === 'home' && 'Richiedi intervento'}
          {flow === 'straordinaria' && 'Pulizia straordinaria'}
          {flow === 'urgente' && 'Manutenzione urgente'}
          {flow === 'condivisa' && 'Manutenzione condivisa'}
        </h1>
      </div>

      {flow === 'home' && <ChoiceHome onPick={setFlow} requests={requests} data-id="element-4119" />}

      {(flow === 'straordinaria' || flow === 'urgente') && <ProviderSearchFlow kind={flow} providers={providers} onConfirm={input => {
      const req = createRequest(input.request);
      if (input.providerId) assignProvider(req.id, input.providerId);
      setFlow('home');
    }} data-id="element-4120" />}

      {flow === 'condivisa' && <SharedFlow openAggregations={openAggregations} onJoin={id => joinAggregation(id)} onCreate={input => {
      createRequest(input);
      setFlow('home');
    }} data-id="element-4121" />}
    </MobileLayout>;
}
// ---- Home: three choices + Le mie richieste ----
function ChoiceHome({
  onPick,
  requests
}: {
  onPick: (f: Flow) => void;
  requests: MaintenanceRequest[];
}) {
  const choices = [{
    flow: 'straordinaria' as Flow,
    icon: Sparkles,
    title: 'Pulizia straordinaria',
    desc: 'Cerchiamo cleaner liberi vicino a te.'
  }, {
    flow: 'condivisa' as Flow,
    icon: Users,
    title: 'Manutenzione condivisa',
    desc: 'Aspetta altri host della zona e risparmi insieme.'
  }, {
    flow: 'urgente' as Flow,
    icon: Zap,
    title: 'Manutenzione urgente',
    desc: 'Troviamo subito un fornitore disponibile in zona.'
  }];
  return <>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8" data-id="element-4122">
        {choices.map(c => {
        const Icon = c.icon;
        return <button key={c.flow} onClick={() => onPick(c.flow)} className="text-left bg-white border border-border rounded-xl p-5 hover:border-coral/50 hover:shadow-sm transition-all" data-id="element-4123">
              <div className="w-11 h-11 rounded-xl bg-coral/10 flex items-center justify-center mb-3" data-id="element-4124">
                <Icon size={22} className="text-coral" data-id="element-4125" />
              </div>
              <h3 className="text-sm font-semibold text-foreground mb-1" data-id="element-4126">
                {c.title}
              </h3>
              <p className="text-xs text-muted-foreground" data-id="element-4127">{c.desc}</p>
            </button>;
      })}
      </div>

      <h2 className="text-sm font-semibold text-foreground mb-3" data-id="element-4128">
        Le mie richieste
      </h2>
      {requests.length > 0 ? <div className="space-y-3" data-id="element-4129">
          {requests.map(r => <Card key={r.id} data-id="element-4130">
              <CardContent className="flex items-start justify-between gap-3" data-id="element-4131">
                <div className="min-w-0" data-id="element-4132">
                  <div className="flex items-center gap-2 mb-1" data-id="element-4133">
                    <h3 className="text-sm font-semibold text-foreground truncate" data-id="element-4134">
                      {r.title}
                    </h3>
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border flex-shrink-0 ${STATUS_BADGE[r.status].cls}`} data-id="element-4135">
                      {STATUS_BADGE[r.status].label}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap" data-id="element-4136">
                    <span className="flex items-center gap-1" data-id="element-4137">
                      <MapPin size={12} data-id="element-4138" />
                      {r.property} · {r.zone}
                    </span>
                    {r.aggregation && <span className="text-coral font-medium" data-id="element-4139">
                        {r.aggregation.filled}/{r.aggregation.total} host
                      </span>}
                    {r.providerName && <span data-id="element-4140">· {r.providerName}</span>}
                  </div>
                </div>
                <span className="text-sm font-semibold text-foreground flex-shrink-0" data-id="element-4141">
                  €{r.price}
                </span>
              </CardContent>
            </Card>)}
        </div> : <div className="bg-white border border-border rounded-xl p-10 text-center" data-id="element-4142">
          <p className="text-sm font-medium text-foreground" data-id="element-4143">
            Nessuna richiesta ancora
          </p>
          <p className="text-xs text-muted-foreground mt-1" data-id="element-4144">
            Scegli un tipo di intervento qui sopra per iniziare.
          </p>
        </div>}
    </>;
}
// ---- Provider search flow (straordinaria / urgente) ----
function ProviderSearchFlow({
  kind,
  providers,
  onConfirm
}: {
  kind: 'straordinaria' | 'urgente';
  providers: Provider[];
  onConfirm: (input: {
    request: Parameters<ReturnType<typeof useMaintenance>['createRequest']>[0];
    providerId?: string | null;
  }) => void;
}) {
  const [property, setProperty] = useState(PROPERTIES[0]);
  const [detail, setDetail] = useState('');
  const [showResults, setShowResults] = useState(false);
  const zone = PROPERTY_ZONE[property];
  const matches = providers.filter(p => p.availability !== 'off').filter(p => kind === 'urgente' ? true : p.trade === 'Tuttofare' || true).sort((a, b) => a.distanceKm - b.distanceKm).slice(0, 3);
  if (!showResults) {
    return <Card data-id="element-4145">
        <CardContent className="space-y-4" data-id="element-4146">
          <div data-id="element-4147">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-4148">
              Proprietà
            </label>
            <select value={property} onChange={e => setProperty(e.target.value)} className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-coral focus:border-transparent outline-none bg-white min-h-[44px]" data-id="element-4149">
              {PROPERTIES.map(p => <option key={p} value={p} data-id="element-4150">
                  {p}
                </option>)}
            </select>
          </div>
          <div data-id="element-4151">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5" data-id="element-4152">
              Cosa serve
            </label>
            <textarea value={detail} onChange={e => setDetail(e.target.value)} rows={3} placeholder={kind === 'straordinaria' ? 'Es. Deep cleaning prima del prossimo check-in' : 'Es. Perdita rubinetto in cucina'} className="w-full px-3 py-2.5 border border-border rounded-lg text-sm focus:ring-2 focus:ring-coral focus:border-transparent outline-none resize-none" data-id="element-4153" />
          </div>
          <Button className="w-full" onClick={() => setShowResults(true)} data-id="element-4154">
            Cerca fornitori in zona
          </Button>
        </CardContent>
      </Card>;
  }
  return <>
      <p className="text-sm text-muted-foreground mb-4" data-id="element-4155">
        {matches.length} fornitori disponibili vicino a{' '}
        <span className="font-medium text-foreground" data-id="element-4156">{property}</span>
      </p>
      <div className="space-y-3" data-id="element-4157">
        {matches.map(p => <Card key={p.id} data-id="element-4158">
            <CardContent data-id="element-4159">
              <div className="flex items-start justify-between gap-3 mb-2" data-id="element-4160">
                <div className="min-w-0" data-id="element-4161">
                  <h3 className="text-sm font-semibold text-foreground" data-id="element-4162">
                    {p.name}
                  </h3>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5 flex-wrap" data-id="element-4163">
                    <span className="font-medium text-foreground" data-id="element-4164">
                      {p.trade}
                    </span>
                    <span className="inline-flex items-center gap-1" data-id="element-4165">
                      <Star size={12} className="text-amber-500" data-id="element-4166" />
                      {p.rating}
                    </span>
                    <span className="inline-flex items-center gap-1" data-id="element-4167">
                      <CalendarClock size={12} data-id="element-4168" />
                      {p.firstSlot}
                    </span>
                  </div>
                </div>
                <span className="text-sm font-semibold text-foreground flex-shrink-0" data-id="element-4169">
                  €{kind === 'straordinaria' ? 120 : 90}
                </span>
              </div>
              <MatchReason distanceKm={p.distanceKm} data-id="element-4170" />
            </CardContent>
            <CardFooter data-id="element-4171">
              <span className="text-xs text-muted-foreground" data-id="element-4172">
                Prima disponibilità {p.firstSlot}
              </span>
              <Button size="sm" onClick={() => onConfirm({
            request: {
              title: kind === 'straordinaria' ? 'Pulizia straordinaria' : detail || 'Manutenzione urgente',
              detail,
              property,
              zone,
              type: kind === 'straordinaria' ? 'pulizia-straordinaria' : 'manutenzione',
              trade: kind === 'urgente' ? p.trade : undefined,
              urgency: 'urgente',
              price: kind === 'straordinaria' ? 120 : 90,
              date: p.firstSlot,
              providerId: p.id
            },
            providerId: p.id
          })} data-id="element-4173">
                Conferma
              </Button>
            </CardFooter>
          </Card>)}
      </div>
    </>;
}
// ---- Shared maintenance flow ----
function SharedFlow({
  openAggregations,
  onJoin,
  onCreate
}: {
  openAggregations: MaintenanceRequest[];
  onJoin: (id: string) => void;
  onCreate: (input: Parameters<ReturnType<typeof useMaintenance>['createRequest']>[0]) => void;
}) {
  const [joined, setJoined] = useState<string[]>([]);
  return <>
      <p className="text-sm text-muted-foreground mb-4" data-id="element-4174">
        Unisciti a una richiesta aperta nella tua zona: raggiunto il numero
        minimo di host, l'intervento viene pianificato e il costo è condiviso.
      </p>
      <div className="space-y-3 mb-8" data-id="element-4175">
        {openAggregations.map(a => {
        const isJoined = joined.includes(a.id);
        const missing = a.aggregation ? a.aggregation.total - a.aggregation.filled : 0;
        return <Card key={a.id} data-id="element-4176">
              <CardContent data-id="element-4177">
                <h3 className="text-sm font-semibold text-foreground mb-1" data-id="element-4178">
                  {a.title}
                </h3>
                <p className="text-xs text-muted-foreground mb-2" data-id="element-4179">
                  {a.trade} in zona {a.zone}
                </p>
                {a.aggregation && <div className="flex items-center gap-4 flex-wrap text-xs" data-id="element-4180">
                    <span className="font-medium text-foreground" data-id="element-4181">
                      {a.aggregation.filled}/{a.aggregation.total} host
                      aggregati
                    </span>
                    {missing > 0 && <span className="font-medium text-coral" data-id="element-4182">
                        Ne mancano {missing}
                      </span>}
                    <span className="inline-flex items-center gap-1 text-muted-foreground" data-id="element-4183">
                      <CalendarClock size={12} data-id="element-4184" />
                      Stima: {a.aggregation.estimateDate}
                    </span>
                  </div>}
              </CardContent>
              <CardFooter data-id="element-4185">
                <span className="text-xs text-muted-foreground" data-id="element-4186">
                  €{a.price} a host (stima)
                </span>
                {isJoined ? <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600" data-id="element-4187">
                    <Check size={15} data-id="element-4188" />
                    Unito
                  </span> : <Button size="sm" onClick={() => {
              onJoin(a.id);
              setJoined(p => [...p, a.id]);
            }} data-id="element-4189">
                    Unisciti
                  </Button>}
              </CardFooter>
            </Card>;
      })}
      </div>

      <div className="bg-white border border-dashed border-border rounded-xl p-5 text-center" data-id="element-4190">
        <h3 className="text-sm font-semibold text-foreground mb-1" data-id="element-4191">
          Non trovi quello che cerchi?
        </h3>
        <p className="text-xs text-muted-foreground mb-4" data-id="element-4192">
          Apri una nuova richiesta condivisa: resta in attesa finché altri host
          si uniscono.
        </p>
        <Button variant="outline" onClick={() => onCreate({
        title: 'Nuova manutenzione condivisa',
        detail: '',
        property: PROPERTIES[0],
        zone: PROPERTY_ZONE[PROPERTIES[0]],
        type: 'manutenzione',
        urgency: 'normale',
        price: 60
      })} data-id="element-4193">
          Apri una nuova richiesta
        </Button>
      </div>
    </>;
}